// UCC tab — lien search history. Each row carries a source chip
// ("Baselayer" info chip deep-linking to the check's Results Explorer,
// or the muted manual provider) and, on Baselayer rows, a refresh action
// that re-fetches the check's authoritative state.

import { Link } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { Table, THead, TBody, TR, TH, TD, Badge, IconBtn } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import { useRefreshBackgroundCheck } from "@/hooks/backgroundChecks/useBackgroundChecks";
import { portfolioAdminKeys } from "@/hooks/portfolioAdmin/usePortfolioAdmin";
import { calendarDate } from "../common/colorBand";
import type { UccLienSearchDto } from "@/api/portfolioAdmin/types";

interface LienSearchHistoryTableProps {
  loanId: string;
  searches: UccLienSearchDto[];
}

export function LienSearchHistoryTable({
  loanId,
  searches,
}: LienSearchHistoryTableProps) {
  const auth = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const canReadBgc =
    auth.hasPermission("BackgroundCheck.Read") || auth.hasRoleCode("ADMIN");
  const refresh = useRefreshBackgroundCheck();

  const onRefresh = async (backgroundCheckId: string) => {
    try {
      await refresh.mutateAsync(backgroundCheckId);
      await queryClient.invalidateQueries({
        queryKey: portfolioAdminKeys.uccSearches(loanId),
      });
      toast.success("Refreshed from Baselayer.");
    } catch {
      toast.error("Could not refresh from Baselayer.");
    }
  };

  return (
    <Table data-testid="ucc-searches-table">
      <THead>
        <TR>
          <TH className="text-white-pure">Source</TH>
          <TH className="text-white-pure">Purpose</TH>
          <TH className="text-white-pure">Jurisdiction</TH>
          <TH className="text-white-pure">Ordered</TH>
          <TH className="text-white-pure">Results</TH>
          <TH className="text-white-pure">Variance</TH>
          <TH className="text-white-pure">
            <span className="sr-only">Actions</span>
          </TH>
        </TR>
      </THead>
      <TBody>
        {searches.map((s) => (
          <TR key={s.id} data-testid={`ucc-search-row-${s.id}`}>
            <TD>
              {s.backgroundCheckId ? (
                <Link
                  to={`/background-checks/${s.backgroundCheckId}`}
                  aria-label="Open the Baselayer check for this search"
                  data-testid={`ucc-search-source-baselayer-${s.id}`}
                >
                  <Badge status="info">Baselayer</Badge>
                </Link>
              ) : (
                <span
                  className="text-caption text-gray-mid"
                  data-testid={`ucc-search-source-manual-${s.id}`}
                >
                  {s.searchProvider || "Manual"}
                </span>
              )}
            </TD>
            <TD>{s.searchPurpose}</TD>
            <TD>{s.searchedJurisdiction}</TD>
            <TD>{calendarDate(s.searchOrderedDate)}</TD>
            <TD>{s.searchResultsCount}</TD>
            <TD>
              {s.varianceFlag ? (
                <Badge status="error">Variance</Badge>
              ) : (
                <Badge status="finalized">OK</Badge>
              )}
            </TD>
            <TD>
              {s.backgroundCheckId && canReadBgc && (
                <IconBtn
                  label="Refresh this search from Baselayer"
                  onClick={() => void onRefresh(s.backgroundCheckId!)}
                  disabled={refresh.isPending}
                  data-testid={`ucc-search-refresh-${s.id}`}
                >
                  <RefreshCw size={14} aria-hidden />
                </IconBtn>
              )}
            </TD>
          </TR>
        ))}
      </TBody>
    </Table>
  );
}
